import type { BusinessApiClient } from '@infra/api/BusinessApiClient';
import { gardeEcriturePeriodeCloturee } from '@infra/tenancy/ecriturePeriodeGuard';
import { TenantScopedRepository } from '@infra/tenancy/TenantScopedRepository';
import type { Salarie } from '@shared/types/Salarie';
import type { Bulletin } from '@shared/types/Bulletin';
import type { Ecriture } from '@shared/types/Ecriture';
import type { Facture } from '@shared/types/Facture';
import type { CentreAnalytique } from '@shared/types/CentreAnalytique';
import type { IPersistencePort } from '@infra/persistence/IPersistencePort';
import { BUSINESS_API_DEFERRED_REASON } from '@infra/persistence/IPersistencePort';
import { RemoteHttpEntityRepository } from './RemoteHttpEntityRepository';
import { CachedRemoteEntityRepository } from '@infra/sync/CachedRemoteEntityRepository';
import { RemoteSyncStore } from '@infra/sync/RemoteSyncStore';
import { RemotePersistenceOfflineError } from './RepositoriesBundle';

/** Mode distant : persistance via nexxus-business-api avec cache SQLite chiffré hors ligne. */
export class RemoteRepositoriesAdapter implements IPersistencePort {
  readonly dataMode = 'REMOTE_MULTI_STATION' as const;
  readonly salaries: TenantScopedRepository<Salarie>;
  readonly bulletins: TenantScopedRepository<Bulletin>;
  readonly ecritures: TenantScopedRepository<Ecriture>;
  readonly factures: TenantScopedRepository<Facture>;
  readonly centresAnalytiques: TenantScopedRepository<CentreAnalytique>;
  readonly offlineCacheEnabled: boolean;

  constructor(
    private readonly api: BusinessApiClient,
    tenantId: string,
    useOfflineCache: boolean,
  ) {
    const makeRepo = <T extends { id: string; tenant_id: string }>(resource: string) => {
      if (useOfflineCache) {
        return new CachedRemoteEntityRepository<T>(resource, tenantId, api);
      }
      return new RemoteHttpEntityRepository<T>(resource, api);
    };

    this.salaries = new TenantScopedRepository(makeRepo<Salarie>('salaries'), 'salaries');
    this.bulletins = new TenantScopedRepository(makeRepo<Bulletin>('bulletins'), 'bulletins');
    this.ecritures = new TenantScopedRepository(
      makeRepo<Ecriture>('ecritures'),
      'ecritures',
      gardeEcriturePeriodeCloturee,
    );
    this.factures = new TenantScopedRepository(makeRepo<Facture>('factures'), 'factures');
    this.centresAnalytiques = new TenantScopedRepository(
      makeRepo<CentreAnalytique>('centres-analytiques'),
      'centres-analytiques',
    );
    this.offlineCacheEnabled = useOfflineCache;
  }

  static async create(
    api: BusinessApiClient,
    tenantId: string,
  ): Promise<RemoteRepositoriesAdapter> {
    const useCache = await RemoteSyncStore.isEnabled();
    return new RemoteRepositoriesAdapter(api, tenantId, useCache);
  }

  isRemoteOnline(): boolean {
    return this.api.isOnline();
  }

  async ensureWriteReady(): Promise<void> {
    if (this.offlineCacheEnabled) {
      return;
    }
    const ok = await this.api.confirmWriteReady();
    if (!ok) throw new RemotePersistenceOfflineError();
    if (!this.api.hasUserSession()) {
      throw new RemotePersistenceOfflineError(
        'Session utilisateur requise pour modifier les données.',
      );
    }
  }

  describe() {
    const base = this.api.getBaseUrl();
    return {
      dataMode: this.dataMode,
      engine: 'remote-api' as const,
      dexiePrimary: false,
      remoteApiBlocked: !base,
      remoteApiReason: base ? undefined : BUSINESS_API_DEFERRED_REASON,
      offlineCacheEnabled: this.offlineCacheEnabled,
    };
  }
}
